import React, { useState, useEffect } from 'react';
import Button from '../../UI/Button';
import Loader from '../../UI/Loader';
import { ColumnMapping, ValidatedRow, ImportResult } from '../ImportWizard';
import { ReferenceData, findIdByName, findOptionValueByName, parseRaceOptionValues } from '../../../services/Referencedataservice';

import {
    updateImportProgress,
    saveImportResult,
    getSession,
    deleteSession,
    saveCompletedUnit
} from '../../../services/importSessionService';

import { createClient, updateClient } from '../../../services/clientsService';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';

interface ImportExecutionStepProps {
    validatedRows: ValidatedRow[];
    columnMappings: ColumnMapping[];
    referenceData: ReferenceData;
    onComplete: (result: ImportResult) => void;
    onBack: () => void;
    onReset: () => void;
    sessionId: string | null;
}

const normalizeAdditionalInfoOptionValue = (value: any): string =>
    String(value ?? '').trim().toLowerCase();

const additionalInfoOptionMatches = (option: any, normalizedValue: string): boolean =>
    [option?.label, option?.value].some(
        optionValue => normalizeAdditionalInfoOptionValue(optionValue) === normalizedValue
    );

const parseAdditionalInfoOptionValues = (value: any): string[] =>
    String(value ?? '')
        .split(/[|,;]/)
        .map(normalizeAdditionalInfoOptionValue)
        .filter(Boolean);

const findAdditionalInfoOption = (options: any[] | undefined, value: any): any | undefined => {
    const normalizedValue = normalizeAdditionalInfoOptionValue(value);
    if (!normalizedValue) {
        return undefined;
    }

    return options?.find(option => additionalInfoOptionMatches(option, normalizedValue));
};

const uniqueRaceIds = (raceIds: Array<number | string>): Array<number | string> =>
    Array.from(new Set(raceIds));

const ImportExecutionStep: React.FC<ImportExecutionStepProps> = ({
    validatedRows,
    columnMappings,
    referenceData,
    onComplete,
    onBack,
    onReset,
    sessionId,
}) => {
    const [importing, setImporting] = useState(false);
    const [progress, setProgress] = useState(0);
    const [currentRow, setCurrentRow] = useState(0);
    const [result, setResult] = useState<ImportResult | null>(null);

    const [canResume, setCanResume] = useState(false);
    const [startFromRow, setStartFromRow] = useState(0);
    const abortRef = React.useRef(false);

    const navigate = useNavigate();

    useEffect(() => {

        const initializeImport = async () => {

            if (!sessionId) {
                startImport(0);
                return;
            }

            try {

                const session = await getSession(sessionId);

                if (!session) {
                    startImport(0);
                    return;
                }

                // If already completed
                if (session.importResult) {

                    setResult(session.importResult);
                    setProgress(100);
                    return;

                }

                // If importing was in progress
                if (session.status === 'importing') {

                    setProgress(session.importProgress || 0);

                    const lastRow = session.currentRow || 0;

                    setStartFromRow(lastRow);
                    setCurrentRow(lastRow);

                    setCanResume(true);

                    return;
                }

                // First time start
                startImport(0);

            } catch (err) {

                console.error("Session load failed:", err);

                startImport(0);

            }

        };

        initializeImport();

        return () => {

            // STOP import when leaving page
            abortRef.current = true;

        };

    }, []);

    const getFieldTypeSample = (field: any): string => {

        switch (field.fieldTypeId) {

            case 1:
            case 9:
            case 16:        
                return 'Text';

            case 7:
                return 'Multiline Text';

            case 2:
                return 'Number';

            // Radio, Select
            case 3:
            case 6:
                return 'Options';
            case 5:
                return 'CheckboxGroup';
            case 4:
                return 'Checkbox';

            case 13:
                return 'Date';

            case 11:
                return 'Notes';

            case 12:
                return 'Minutes';

            default:
                return '';
        }
    };

    const getOptionsStr = (option: any): string => {
        if (!option) return '';
        const rStr = JSON.stringify({
            "label": option.label ?? option.value ?? option,
            "value": option.value ?? option.label ?? option
        });
        return rStr;
    }

    const getCheckbox = (checkboxStr: any): any => {
        if (!checkboxStr) return '';
        return true;
    }
    const getCheckboxGroup = (checkboxGroup: any) => {
        if (!checkboxGroup.selectedOption) return '[]';
        const jsonArray = checkboxGroup.selectedOption.map((checkboxOption: any) => {
            return {"label": checkboxOption.label ?? checkboxOption.value,"value": checkboxOption.value ?? checkboxOption.label};
        });
        //console.log("jsonArray: ", jsonArray);
        return JSON.stringify(jsonArray);
    }

    const convertDateFormat = (dateStr: string): string => {
        if (!dateStr) return '';
        
        // If in MM/dd/yyyy format
        if (/^\d{1,2}\/\d{1,2}\/\d{4}$/.test(dateStr)) {
            const parts = dateStr.split('/');
            // Check if it's MM/dd/yyyy (month first) or dd/MM/yyyy (day first)
            // Assume MM/dd/yyyy if first number is > 12, otherwise dd/MM/yyyy
            const firstNum = parseInt(parts[0]);
            const secondNum = parseInt(parts[1]);
            
            if (firstNum > 12) {
                // Must be dd/MM/yyyy
                const [day, month, year] = parts;
                return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
            } else if (secondNum > 12) {
                // Must be MM/dd/yyyy
                const [month, day, year] = parts;
                return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
            } else {
                // Ambiguous - default to dd/MM/yyyy
                const [day, month, year] = parts;
                return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
            }
        }
        
        return dateStr;
    };

    const extractHouseholdMembers = (rowData: Record<string, any>): any[] => {
        const householdMembers: Record<number, any> = {};
        const hhPattern = /^hh(\d+)_(.+)$/i;

        Object.keys(rowData).forEach(key => {
            const match = key.match(hhPattern);
            if (match) {
                const hhNumber = parseInt(match[1]);
                const fieldName = match[2];
                
                if (!householdMembers[hhNumber]) {
                    householdMembers[hhNumber] = {};
                }
                
                householdMembers[hhNumber][fieldName] = rowData[key];
            }
        });

        const members: any[] = [];
        Object.keys(householdMembers).sort((a, b) => parseInt(a) - parseInt(b)).forEach(hhNum => {
            const member = householdMembers[parseInt(hhNum)];
            
            // Only add if member has at least firstName or lastName
            if (member.firstName || member.lastName) {
                const hhData: any = {
                    firstName: member.firstName || '',
                    middleName: member.middleName || '',
                    lastName: member.lastName || '',
                    dateOfBirth: convertDateFormat(member.dateOfBirth || '') || null,
                };

                // Add gender
                if (member.genderId) {
                    const genderId = typeof member.genderId === 'number' 
                        ? member.genderId 
                        : Number(findOptionValueByName(member.genderId, referenceData.genderOptions));
                    if (genderId) hhData.genderId = genderId;
                }

                // Add ethnicity
                if (member.ethnicityId) {
                    const ethnicityId = typeof member.ethnicityId === 'number'
                        ? member.ethnicityId
                        : Number(findOptionValueByName(member.ethnicityId, referenceData.ethnicityOptions, { allowSubstring: true }));
                    if (ethnicityId) hhData.ethnicityId = ethnicityId;
                }

                // Add race(s)
                if (member.raceIds) {
                    const raceSelection = typeof member.raceIds === 'string'
                        ? parseRaceOptionValues(member.raceIds, referenceData)
                        : {
                            ethnicityId: undefined,
                            raceIds: Array.isArray(member.raceIds)
                                ? member.raceIds
                                : [member.raceIds]
                        };
                    if (raceSelection.ethnicityId) hhData.ethnicityId = raceSelection.ethnicityId;
                    const raceIds = uniqueRaceIds(raceSelection.raceIds);
                    if (raceIds.length > 0) hhData.raceIds = raceIds;
                }

                // Add relationship
                if (member.relationshipId) {
                    const relationshipId = typeof member.relationshipId === 'number'
                        ? member.relationshipId
                        : findIdByName(member.relationshipId, referenceData.relationships);
                    if (relationshipId) hhData.relationshipId = relationshipId;
                } else {
                    hhData.relationshipId = null;
                }

                //Add linked client id
                if (member.linkedClientId) {
                    hhData.linkedClientId = member.linkedClientId;
                }

                // Add contact info
                if (member.phoneNumber) hhData.phoneNumber = member.phoneNumber;
                if (member.email) hhData.email = member.email;

                members.push(hhData);
            }
        });

        return members;
    };

    const extractAdditionalInfoFields = async (rowData: Record<string, any>): Promise<any[]> => {
        const additionalInfoFields: any[] = [];
        const additionalInfoPattern = /^additionalInfo_(.+)$/;
        
        //console.log("rowData: ", rowData);
        //console.log("referenceData.clientform: ", referenceData.clientform);

        for (const key of Object.keys(rowData)) {
            const match = key.match(additionalInfoPattern);
            if (match) {
                const fieldId = match[1];
                //now as we have id get the fieldTypeId
                const additionalField = referenceData.clientform.find(f => f.id === fieldId);
                //now check the fieldType
                if(!additionalField)
                    continue;

                const fieldType = getFieldTypeSample(additionalField);
                //additional field value
                const value = rowData[key];

                if(fieldType==='CheckboxGroup'){
                    //check for value and then find the selected option
                    const selectedValues = parseAdditionalInfoOptionValues(value);

                    const additionalInfoMatches =
                    (additionalField as any).options?.filter((opt: any) =>
                        selectedValues.some(
                            selectedValue => additionalInfoOptionMatches(opt, selectedValue)
                        )
                    ) || [];
                    
                    const rValue = getCheckboxGroup({'fieldId': fieldId, "selectedOption": additionalInfoMatches});
                    //console.log("rValue: ", rValue);
                    
                    additionalInfoFields.push({
                        fieldId: fieldId,
                        value: rValue,
                        files: []
                    });
                } else if(fieldType==='Options'){
                    const additionalInfoMatch = findAdditionalInfoOption((additionalField as any).options, value);
                    let rValue: any = '';
                    if(additionalInfoMatch)
                    rValue = getOptionsStr(additionalInfoMatch);
                    additionalInfoFields.push({
                        fieldId: fieldId,
                        value: rValue,
                        files: []
                    });
                } else if(fieldType==='Checkbox'){
                    //const rValue = getCheckbox(value);
                    let rValue: any = '';
                    if(value){
                        rValue = true;
                    }
                    additionalInfoFields.push({
                        fieldId: fieldId,
                        value: rValue,
                        files: []
                    });
                } else {
                    if (value !== undefined && value !== null && value !== '') {
                        additionalInfoFields.push({
                            fieldId: fieldId,
                            value: String(value),
                            files: []
                        });
                    }
                }
            }
        }

        return additionalInfoFields;
    };

    const buildParticipantPayload = async (rowData: Record<string, any>): Promise<any> => {
        const additionalInfoFields = await extractAdditionalInfoFields(rowData);
        const householdId = rowData.householdId === undefined || rowData.householdId === null || rowData.householdId === ''
            ? undefined
            : Number(rowData.householdId);
        
        const payload: any = {
            firstName: rowData.firstName,
            lastName: rowData.lastName,
            dateOfBirth: convertDateFormat(rowData.dateOfBirth) || null,
            activated: convertDateFormat(rowData.activationDate),
            householdMembers: extractHouseholdMembers(rowData),
            enrollInPrograms: [],
        };

        if (typeof householdId === 'number' && Number.isInteger(householdId) && householdId > 0) {
            payload.householdId = householdId;
        }

        // Handle program enrollment (supports pipe-delimited multiple programs)
        if (rowData.programName) {
            const programNames = rowData.programName.split('|').map((p: string) => p.trim()).filter(Boolean);
            const enrollmentDates = rowData.enrollmentDate 
                ? rowData.enrollmentDate.split('|').map((d: string) => d.trim()).filter(Boolean)
                : [];
            
            const programs: any[] = [];
            
            programNames.forEach((programName: string, index: number) => {
                const program = referenceData.programs.find(
                    p => p.name.toLowerCase() === programName.toLowerCase()
                );
                if (program) {
                    // Use specific date if available, otherwise use first date, otherwise use today
                    let enrollDate;
                    if (enrollmentDates.length === programNames.length) {
                        enrollDate = enrollmentDates[index];
                    } else if (enrollmentDates.length === 1) {
                        enrollDate = enrollmentDates[0];
                    } else {
                        enrollDate = new Date().toISOString().split('T')[0];
                    }
                    
                    programs.push({
                        programId: program.id,
                        enrollmentDate: convertDateFormat(enrollDate)
                    });
                }
            });
            
            payload.enrollInPrograms = programs;
        }

        // Add additional info if any fields are mapped
        if (additionalInfoFields.length > 0 && referenceData.clientform.length > 0) {

            //console.log("additionalInfoFields: ", additionalInfoFields);

            payload.additionalInfoFormId = referenceData.clientform[0].formId;
            payload.additionalInfoFormVersionId = referenceData.clientform[0].versionId;
            payload.additionalInfoFields = additionalInfoFields;
        }

        // Optional fields
        if (rowData.middleName) payload.middleName = rowData.middleName;
        if (rowData.email) payload.email = rowData.email;
        if (rowData.phoneNumber) payload.phoneNumber = rowData.phoneNumber;
        if (rowData.pronouns) payload.pronouns = rowData.pronouns;
        if (rowData.addressLine1) payload.addressLine1 = rowData.addressLine1;
        if (rowData.addressLine2) payload.addressLine2 = rowData.addressLine2;
        if (rowData.addressCity) payload.addressCity = rowData.addressCity;
        if (rowData.addressState) payload.addressState = rowData.addressState;
        if (rowData.addressZipCode) payload.addressZipCode = rowData.addressZipCode;
        
        // Handle gender ID
        if (rowData.genderId) {
            const genderId = typeof rowData.genderId === 'number' 
                ? rowData.genderId 
                : Number(findOptionValueByName(rowData.genderId, referenceData.genderOptions));
            if (genderId) payload.genderId = genderId;
        }

        // Handle ethnicity ID
        if (rowData.ethnicityId) {
            const ethnicityId = typeof rowData.ethnicityId === 'number'
                ? rowData.ethnicityId
                : Number(findOptionValueByName(rowData.ethnicityId, referenceData.ethnicityOptions, { allowSubstring: true }));
            if (ethnicityId) payload.ethnicityId = ethnicityId;
        }

        // Handle race IDs
        if (rowData.raceIds) {
            const raceSelection = typeof rowData.raceIds === 'string'
                ? parseRaceOptionValues(rowData.raceIds, referenceData)
                : {
                    ethnicityId: undefined,
                    raceIds: Array.isArray(rowData.raceIds)
                        ? rowData.raceIds
                        : [rowData.raceIds]
                };
            if (raceSelection.ethnicityId) payload.ethnicityId = raceSelection.ethnicityId;
            const raceIds = uniqueRaceIds(raceSelection.raceIds);
            if (raceIds.length > 0) payload.raceIds = raceIds;
        }

        return payload;
    };

    interface HouseholdGroup {
        key: string;
        rows: ValidatedRow[];
    }

    const groupRowsByHouseholdKey = (rows: ValidatedRow[]): {
        groups: HouseholdGroup[];
        standaloneRows: ValidatedRow[];
    } => {
        const grouped: Record<string, ValidatedRow[]> = {};
        const standaloneRows: ValidatedRow[] = [];

        rows.forEach(row => {
            const key = String(row.data.householdKey || row.data.householdId || '').trim();
            if (key) {
                if (!grouped[key]) {
                    grouped[key] = [];
                }
            
                grouped[key].push(row);
            
            } else {
                standaloneRows.push(row);
            }
        });

        const groups: HouseholdGroup[] = Object.entries(grouped).map(([key, rows]) => ({ key, rows }));

        return { groups, standaloneRows };
    }

    const startImport = async (startIndex = 0) => {
        setImporting(true);
        const sessions = sessionId ? await getSession(sessionId) : null;
        const completedGroupKeys = sessions?.completedGroupKeys || [];
        const completedStandaloneIndices = sessions?.completedStandaloneIndices || [];
        abortRef.current = false;
        const errors: any[] = [];
        let successCount = 0;
        let skippedCount = 0;
        const batchSize = 5;
        const delayBetweenBatches = 3000;

        // Filter to only valid rows for import
        const validRowsToImport = validatedRows.filter(row => row.isValid);
        const invalidRowsSkipped = validatedRows.filter(row => !row.isValid);
        skippedCount = invalidRowsSkipped.length;

        const { groups, standaloneRows } = groupRowsByHouseholdKey(validRowsToImport);

        // flatten all work units into a single ordered list for progress tracking
        // each standalone = 1 unit, each group = 1 unit
        const totalUnits = standaloneRows.length + groups.length;
        let processedUnits = 0;

        const updateProgress = async (processed: number) => {
            const percent = Math.round((processed / totalUnits) * 100);
            setProgress(percent);
            if (sessionId) {
                try {
                    await updateImportProgress(sessionId, percent, processed);
                } catch (err) {
                    console.error('Error saving progress:', err);
                }
            }
        };

        
        try {
            
            // --- STANDALONE ROWS ---
            const batchSize = 5;
            const delayBetweenBatches = 3000;
            
            for (let i = 0; i < standaloneRows.length; i += batchSize) {
                if (abortRef.current) return;
                
                const batch = standaloneRows.slice(i, i + batchSize);
                
                const batchPromises = batch.map(async (row, batchIndex) => {
                    const globalIndex = i + batchIndex;
                    setCurrentRow(globalIndex + 1);

                    if (completedStandaloneIndices.includes(row.rowIndex)) {
                        return { success: true, index: globalIndex, rowIndex: row.rowIndex, alreadyCompleted: true };
                    }

                    try {
                        const payload = await buildParticipantPayload(row.data);
                        await createClient(payload);
                        return { success: true, index: globalIndex, rowIndex: row.rowIndex };
                    } catch (error: any) {
                        return {
                            success: false,
                            index: globalIndex,
                            rowIndex: row.rowIndex,
                            error: error.response?.data?.message || error.message || 'Failed to import',
                            data: row.data
                        };
                    }
                });

                const batchResults = await Promise.allSettled(batchPromises);

                batchResults.forEach(result => {
                    if (result.status === 'fulfilled') {
                        const val = result.value;
                        if (val.success) {
                            successCount++;
                            if (sessionId && !val.alreadyCompleted) {
                                saveCompletedUnit(sessionId, { standaloneIndex: val.rowIndex });
                            }
                        } else {
                            errors.push({
                                row: val.index + 1,
                                data: val.data,
                                errors: [{ row: val.index, field: 'general', message: val.error }]
                            });
                        }
                    }
                });

                processedUnits += batch.length;
                await updateProgress(processedUnits);

                if (i + batchSize < standaloneRows.length) {
                    await new Promise(resolve => setTimeout(resolve, delayBetweenBatches));
                }
            }

            // --- HOUSEHOLD GROUPS ---
            for (let i = 0; i < groups.length; i++) {
                if (abortRef.current) return;

                const group = groups[i];

                if (completedGroupKeys.includes(group.key)) {
                    successCount += group.rows.length;
                    processedUnits++;
                    await updateProgress(processedUnits);
                    continue;
                }

                const [headRow, ...memberRows] = group.rows;

                setCurrentRow(standaloneRows.length + i + 1);

                // Step 1 — POST the head
                let headClientId: string;
                try {
                    const headPayload = await buildParticipantPayload(headRow.data);
                    const headResult = await createClient(headPayload);
                    headClientId = headResult.id;
                    successCount++;
                } catch (error: any) {
                    // Head failed — skip entire group
                    const message = error.response?.data?.message || error.message || 'Failed to import';
                    errors.push({
                        row: headRow.rowIndex + 1,
                        data: headRow.data,
                        errors: [{ row: headRow.rowIndex, field: 'general', message: `Head of household failed, group skipped: ${message}` }]
                    });
                    // Report all members as skipped too
                    memberRows.forEach(memberRow => {
                        errors.push({
                            row: memberRow.rowIndex + 1,
                            data: memberRow.data,
                            errors: [{ row: memberRow.rowIndex, field: 'general', message: `Skipped — head of household (${headRow.data.firstName} ${headRow.data.lastName}) failed to import` }]
                        });
                    });
                    processedUnits++;
                    await updateProgress(processedUnits);
                    continue;
                }

                // Step 2 — POST each member
                const linkedMembers: any[] = [];

                for (const memberRow of memberRows) {
                    try {
                        const memberPayload = await buildParticipantPayload(memberRow.data);
                        const memberResult = await createClient(memberPayload);
                        successCount++;
                        const linkedMemberRaceSelection = memberRow.data.raceIds
                            ? parseRaceOptionValues(memberRow.data.raceIds, referenceData)
                            : { ethnicityId: undefined, raceIds: [] };
                        const linkedMemberEthnicityId = linkedMemberRaceSelection.ethnicityId
                            || (memberRow.data.ethnicityId
                                ? Number(findOptionValueByName(memberRow.data.ethnicityId, referenceData.ethnicityOptions, { allowSubstring: true }))
                                : null);

                        // Build the household member entry for the PUT
                        linkedMembers.push({
                            linkedClientId: memberResult.id,
                            firstName: memberRow.data.firstName,
                            middleName: memberRow.data.middleName || null,
                            lastName: memberRow.data.lastName,
                            dateOfBirth: convertDateFormat(memberRow.data.dateOfBirth) || null,
                            genderId: memberRow.data.genderId
                                ? Number(findOptionValueByName(memberRow.data.genderId, referenceData.genderOptions))
                                : null,
                            ethnicityId: linkedMemberEthnicityId,
                            raceIds: uniqueRaceIds(linkedMemberRaceSelection.raceIds),
                            relationshipId: memberRow.data.relationshipId
                                ? findIdByName(memberRow.data.relationshipId, referenceData.relationships)
                                : null,
                            phoneNumber: memberRow.data.phoneNumber || null,
                            email: memberRow.data.email || null,
                        });
                    } catch (error: any) {
                        // Member failed — report but continue with the rest of the group
                        const message = error.response?.data?.message || error.message || 'Failed to import';
                        errors.push({
                            row: memberRow.rowIndex + 1,
                            data: memberRow.data,
                            errors: [{ row: memberRow.rowIndex, field: 'general', message }]
                        });
                    }
                }

                // Step 3 — PUT the head to link all successfully created members
                if (linkedMembers.length > 0) {
                    try {
                        const headPayload = await buildParticipantPayload(headRow.data);
                        await updateClient({
                            ...headPayload,
                            id: headClientId,
                            householdMembers: linkedMembers,
                        });
                    } catch (error: any) {
                        // Members exist as clients but are unlinked
                        const message = error.response?.data?.message || error.message || 'Failed to link';
                        errors.push({
                            row: headRow.rowIndex + 1,
                            data: headRow.data,
                            errors: [{
                                row: headRow.rowIndex,
                                field: 'general',
                                message: `Members were created but could not be linked to household: ${message}`
                            }]
                        });
                    }
                }

                if (sessionId) await saveCompletedUnit(sessionId, { groupKey: group.key });

                processedUnits++;
                await updateProgress(processedUnits);

                // delay between groups
                if (i < groups.length - 1) {
                    await new Promise(resolve => setTimeout(resolve, delayBetweenBatches));
                }
            }

        } catch (error: any) {
            console.error('Import process error:', error);
            toast.error('Import process encountered an error');
        }

        const importResult: ImportResult = {
            totalProcessed: validatedRows.length,
            successCount,
            failedCount: errors.length,
            skippedCount,
            errors,
        };

        setResult(importResult);

        if (sessionId) {
            try {
                await saveImportResult(sessionId, importResult);
            } catch (error) {
                console.error('Error saving result:', error);
            }
        }

        setImporting(false);
        onComplete(importResult);

        if (errors.length === 0) {
            toast.success(`Successfully imported ${successCount} participants`);
        } else {
            toast.error(`Import completed with ${errors.length} errors. ${successCount} participants imported successfully.`);
        }
    };

    const handleDownloadErrorReport = () => {
        if (!result || result.errors.length === 0) return;

        const csvContent = [
            ['Row', 'First Name', 'Last Name', 'Error'].join(','),
            ...result.errors.map((error) =>
                [
                    error.row,
                    `"${error.data.firstName || ''}"`,
                    `"${error.data.lastName || ''}"`,
                    `"${error.errors.map((e) => e.message).join('; ')}"`,
                ].join(',')
            ),
        ].join('\n');

        const blob = new Blob([csvContent], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'import_error_report.csv';
        a.click();
        window.URL.revokeObjectURL(url);
        toast.success('Error report downloaded');
    };

    const handleViewImported = () => {
        navigate('/home');
    };

    const handleDone = async () => {
        if (sessionId) {
            await deleteSession(sessionId);
        }
        onReset();
        window.location.href = '/import';
    };

    return (
        <div className="import-step">
            <h2 className="section-header">Import & Results</h2>

            {canResume && !importing && !result && (

                <div className="card margin-vertical-3">

                    <div className="card-body">

                        <div className="row">

                            <div className="column-6">

                                <Button
                                    id='resume_import'
                                    text="Resume Import"
                                    onClick={() => {

                                        setCanResume(false);

                                        startImport(startFromRow);

                                    }}
                                />

                            </div>

                            <div className="column-6">

                                <Button
                                    id='delete_session'
                                    text="Delete Session"
                                    className="secondary"
                                    onClick={async () => {

                                        if (!sessionId) return;

                                        await deleteSession(sessionId);

                                        toast.success("Session deleted");

                                        onReset();

                                    }}
                                />

                            </div>

                        </div>

                        <p className="margin-top-2">

                            Import paused at row {startFromRow}

                        </p>

                    </div>

                </div>

            )}

            {importing && (
                <div className="card margin-vertical-4">
                    <div className="card-header">
                        <strong>Import Execution</strong>
                    </div>
                    <div className="card-body">
                        <div className="progress-container">
                            <div className="row align-items-center margin-bottom-3">
                                <div className="column">
                                    <strong>Importing Data...</strong>
                                </div>
                                <div className="column-auto">
                                    <strong>{progress}%</strong>
                                </div>
                            </div>
                            <div className="progress-bar">
                                <div
                                    className="progress-fill"
                                    style={{ width: `${progress}%` }}
                                />
                            </div>
                            <div className="row align-items-center margin-top-3">
                                <div className="column-auto">
                                    <Loader noMarginTop={true} />
                                </div>
                                <div className="column-auto">
                                    <span>Processing record {currentRow} of {validatedRows.length}...</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {!importing && result && (
                <>
                    <div className="card margin-vertical-4">
                        <div className="card-header hide">
                            <strong>Import Summary</strong>
                        </div>
                        <div className="card-body">
                            <div className="row align-items-center margin-bottom-3 hide">
                                <div className="column-auto">
                                    <i
                                        className="ri-checkbox-circle-line"
                                        style={{ fontSize: '32px', color: '#16a34a' }}
                                    />
                                </div>
                                <div className="column">
                                    <h3 style={{ margin: 0, color: '#16a34a' }}>
                                        Import Completed!
                                    </h3>
                                </div>
                            </div>

                            <div className="row margin-top-4">
                                <div className="column-6">
                                    <div className="stat-box">
                                        <div className="stat-icon">
                                            <i className="ri-file-list-line" />
                                        </div>
                                        <div className="stat-content">
                                            <div className="stat-label">
                                                Total Rows Processed:
                                            </div>
                                            <div className="stat-value">
                                                {result.totalProcessed}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="column-6">
                                    <div className="stat-box success">
                                        <div className="stat-icon">
                                            <i className="ri-checkbox-circle-line" />
                                        </div>
                                        <div className="stat-content">
                                            <div className="stat-label">
                                                Successfully Imported:
                                            </div>
                                            <div className="stat-value">
                                                {result.successCount}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="row margin-top-3">
                                <div className="column-6">
                                    <div className="stat-box error">
                                        <div className="stat-icon">
                                            <i className="ri-error-warning-line" />
                                        </div>
                                        <div className="stat-content">
                                            <div className="stat-label">Failed During Import:</div>
                                            <div className="stat-value">{result.failedCount}</div>
                                        </div>
                                    </div>
                                </div>
                                <div className="column-6">
                                    <div className="stat-box">
                                        <div className="stat-icon">
                                            <i className="ri-skip-right-line" />
                                        </div>
                                        <div className="stat-content">
                                            <div className="stat-label">Skipped Validation Rows:</div>
                                            <div className="stat-value">{result.skippedCount}</div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="row margin-top-3">
                                <div className="column-6">
                                    <div className="stat-box">
                                        <div className="stat-icon">
                                            <i className="ri-team-line" />
                                        </div>
                                        <div className="stat-content">
                                            <div className="stat-label">
                                                With Household Members:
                                            </div>
                                            <div className="stat-value">
                                                {validatedRows.filter(r => 
                                                    extractHouseholdMembers(r.data).length > 0
                                                ).length}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="row margin-top-4">
                                <div className="column-6 hide">
                                    <Button
                                        id="view-imported"
                                        text="View Imported Records"
                                        icon={<i className="ri-checkbox-circle-line" />}
                                        className="full-width"
                                        onClick={handleViewImported}
                                    />
                                </div>
                                {result.failedCount > 0 && (
                                    <div className="column-6">
                                        <Button
                                            id="download-error-report"
                                            text="Download Error Report (CSV)"
                                            icon={<i className="ri-download-line" />}
                                            className="secondary full-width"
                                            onClick={handleDownloadErrorReport}
                                        />
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    <div className="row justify-content-center margin-top-5">
                        <div className="column-auto">
                            <Button id="done-button" text="Import More Data" onClick={handleDone} />
                        </div>
                    </div>
                </>
            )}
        </div>
    );
};

export default ImportExecutionStep;
